import { ENCORE_IDS } from "../data/trainers.ts";
import { HUNTS } from "../data/secrets.ts";
import type { ItemId } from "../data/items.ts";
import { ZONES, type ZoneId } from "../data/map.ts";
import { peekMove } from "../data/moves.ts";
import { SPECIES, SPECIES_IDS, type SpeciesId } from "../data/species.ts";
import { TYPE_IDS, TYPE_META, type TypeId } from "../data/types.ts";
import { hashSeed, Rng } from "../engine/rng.ts";
import type { Mon } from "./mon.ts";

/**
 * Engagement layer: lifetime stats, three daily missions (plus a live-world bonus), a 7-day login
 * streak and badges with titles. Everything is plain data on the save, and every number only ever
 * grows, so a local save and the server's copy merge by taking the larger of each value.
 */

// ------------------------------------------------------------------------------------------------
// Stats
// ------------------------------------------------------------------------------------------------

export interface Stats {
  caught: number;
  caughtType: Partial<Record<TypeId, number>>;
  wildWins: number;
  trainerWins: number;
  duelWins: number;
  raidDamage: number;
  raidWins: number;
  trades: number;
  marketSales: number;
  marketBuys: number;
  conditions: number;
  superHits: number;
  steps: number;
  rests: number;
  shinies: number;
  missionsDone: number;
  bestStreak: number;
  zoneVisits: Partial<Record<ZoneId, number>>;
}

type CountKey = Exclude<keyof Stats, "caughtType" | "zoneVisits">;
const COUNT_KEYS: CountKey[] = ["caught", "wildWins", "trainerWins", "duelWins", "raidDamage", "raidWins", "trades", "marketSales",
  "marketBuys", "conditions", "superHits", "steps", "rests", "shinies", "missionsDone", "bestStreak"];

export function emptyStats(): Stats {
  return {
    caught: 0, caughtType: {}, wildWins: 0, trainerWins: 0, duelWins: 0, raidDamage: 0, raidWins: 0, trades: 0,
    marketSales: 0, marketBuys: 0, conditions: 0, superHits: 0, steps: 0, rests: 0, shinies: 0, missionsDone: 0,
    bestStreak: 0, zoneVisits: {},
  };
}

const count = (v: unknown): number => (Number.isFinite(v) ? Math.max(0, Math.min(1e9, Math.floor(Number(v)))) : 0);

function countRecord<K extends string>(raw: unknown, keys: readonly K[]): Partial<Record<K, number>> {
  const out: Partial<Record<K, number>> = {};
  if (raw && typeof raw === "object") for (const k of keys) {
    const v = count((raw as Record<string, unknown>)[k]);
    if (v) out[k] = v;
  }
  return out;
}

const ZONE_IDS = Object.keys(ZONES) as ZoneId[];

export function normalizeStats(raw: unknown): Stats {
  const s = emptyStats();
  if (!raw || typeof raw !== "object") return s;
  const r = raw as Record<string, unknown>;
  for (const k of COUNT_KEYS) s[k] = count(r[k]);
  s.caughtType = countRecord(r.caughtType, TYPE_IDS);
  s.zoneVisits = countRecord(r.zoneVisits, ZONE_IDS);
  return s;
}

export function mergeStats(a: Stats, b: Stats): Stats {
  const out = emptyStats();
  for (const k of COUNT_KEYS) out[k] = Math.max(a[k], b[k]);
  for (const t of TYPE_IDS) { const v = Math.max(a.caughtType[t] ?? 0, b.caughtType[t] ?? 0); if (v) out.caughtType[t] = v; }
  for (const z of ZONE_IDS) { const v = Math.max(a.zoneVisits[z] ?? 0, b.zoneVisits[z] ?? 0); if (v) out.zoneVisits[z] = v; }
  return out;
}

// ------------------------------------------------------------------------------------------------
// Daily missions and the login streak
// ------------------------------------------------------------------------------------------------

export type MissionKind = "catch" | "catchType" | "wildWins" | "superHits" | "conditions" | "steps" | "rests" | "visit"
  | "duelWins" | "raidDamage" | "market";

export interface Mission {
  kind: MissionKind;
  target: number;
  /** Stat value when the mission was issued; progress is the growth since. */
  base: number;
  type?: TypeId;
  zone?: ZoneId;
  money: number;
  item?: ItemId;
  count?: number;
  claimed: boolean;
  /** Needs the live world (duels, raids, market). */
  live?: boolean;
}

export interface Daily {
  day: string;
  missions: Mission[];
  streak: number;
  /** The streak reward for `day` has been paid. */
  streakClaimed: boolean;
  /** The all-three bonus has been paid. */
  bonusClaimed: boolean;
}

/** Local calendar day, e.g. "2026-09-23". */
export function dayKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Weeks since Monday 2026-01-05 for a local day key; encore teams change every Monday. */
export function weekOf(day: string): number {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return Math.floor((Date.UTC(y, m - 1, d) - Date.UTC(2026, 0, 5)) / (7 * 86400000));
}

export function prevDay(day: string): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return dayKey(new Date(y, m - 1, d - 1, 12).getTime());
}

/** Streak rewards, a week long, then the cycle repeats. */
export const STREAK_REWARDS: { money: number; item?: ItemId; count?: number; label: string }[] = [
  { money: 200, label: "200 coins" },
  { money: 0, item: "capsule", count: 3, label: "3 Meme Tins" },
  { money: 400, label: "400 coins" },
  { money: 0, item: "gloss", count: 2, label: "2 Gloss Tins" },
  { money: 0, item: "fullcope", count: 1, label: "Full Cope" },
  { money: 700, label: "700 coins" },
  { money: 500, item: "myth", count: 1, label: "Myth Tin + 500 coins" },
];

export const DAILY_BONUS = { money: 300, item: "myth" as ItemId, count: 1 };

export function streakReward(streak: number) {
  return STREAK_REWARDS[(Math.max(1, streak) - 1) % STREAK_REWARDS.length]!;
}

type Goals = { stats: Stats; daily: Daily | null; seed: string; seen: SpeciesId[]; party: Mon[]; pc: Mon[] };

function statFor(state: Goals, m: Pick<Mission, "kind" | "type" | "zone">): number {
  const s = state.stats;
  switch (m.kind) {
    case "catch": return s.caught;
    case "catchType": return s.caughtType[m.type!] ?? 0;
    case "wildWins": return s.wildWins;
    case "superHits": return s.superHits;
    case "conditions": return s.conditions;
    case "steps": return s.steps;
    case "rests": return s.rests;
    case "visit": return s.zoneVisits[m.zone!] ?? 0;
    case "duelWins": return s.duelWins;
    case "raidDamage": return s.raidDamage;
    case "market": return s.marketSales + s.marketBuys;
  }
}

export function missionProgress(state: Goals, m: Mission): number {
  return Math.max(0, Math.min(m.target, statFor(state, m) - m.base));
}

export function missionDone(state: Goals, m: Mission): boolean {
  return missionProgress(state, m) >= m.target;
}

export function missionText(m: Mission): string {
  switch (m.kind) {
    case "catch": return `Catch ${m.target} creature${m.target > 1 ? "s" : ""}`;
    case "catchType": return `Catch ${m.target} ${TYPE_META[m.type!].name}-type creature${m.target > 1 ? "s" : ""}`;
    case "wildWins": return `Win ${m.target} wild battles`;
    case "superHits": return `Land ${m.target} super-effective hits`;
    case "conditions": return `Inflict ${m.target} battle conditions`;
    case "steps": return `Walk ${m.target} steps`;
    case "rests": return "Rest at any kettle";
    case "visit": return `Visit ${ZONES[m.zone!].name}`;
    case "duelWins": return "Win a live duel";
    case "raidDamage": return `Deal ${m.target} raid damage`;
    case "market": return "Buy or sell on the Market";
  }
}

export function rewardText(m: { money: number; item?: ItemId; count?: number }, itemName: (id: ItemId) => string): string {
  const parts: string[] = [];
  if (m.money) parts.push(`${m.money} coins`);
  if (m.item && m.count) parts.push(`${m.count > 1 ? `${m.count} ` : ""}${itemName(m.item)}`);
  return parts.join(" + ");
}

function ownsConditionMove(state: Goals): boolean {
  return [...state.party, ...state.pc].some((mon) => mon.moves.some((s) => peekMove(s.id)?.fx?.kind === "status"));
}

/** Today's missions: deterministic from the save seed and the day, never from the game's RNG. */
export function makeMissions(state: Goals, day: string): Mission[] {
  const rng = new Rng(hashSeed(`${state.seed}:daily:${day}`));
  const seenTypes = [...new Set(state.seen.flatMap((id) => SPECIES[id].types))];
  const visited = ZONE_IDS.filter((z) => (state.stats.zoneVisits[z] ?? 0) > 0 && ZONES[z].encounters.length > 0);
  const pool: Omit<Mission, "base" | "claimed" | "money" | "item" | "count">[] = [
    { kind: "catch", target: rng.int(2, 4) },
    { kind: "wildWins", target: rng.int(4, 7) },
    { kind: "superHits", target: rng.int(4, 6) },
    { kind: "steps", target: rng.int(5, 8) * 50 },
    { kind: "rests", target: 1 },
  ];
  if (seenTypes.length) pool.push({ kind: "catchType", target: rng.int(1, 2), type: rng.pick(seenTypes) });
  if (visited.length > 1) pool.push({ kind: "visit", target: 1, zone: rng.pick(visited) });
  if (ownsConditionMove(state)) pool.push({ kind: "conditions", target: rng.int(2, 4) });
  const picked: typeof pool = [];
  while (picked.length < 3 && pool.length) picked.push(pool.splice(rng.int(0, pool.length - 1), 1)[0]!);
  const items: ItemId[] = ["capsule", "sip", "gloss", "cleartea"];
  const missions: Mission[] = picked.map((p) => {
    const item = rng.chance(0.6) ? rng.pick(items) : undefined;
    return { ...p, base: 0, claimed: false, money: 100 + rng.int(1, 5) * 40, ...(item ? { item, count: item === "gloss" ? 1 : 2 } : {}) };
  });
  const live: Mission[] = [
    { kind: "duelWins", target: 1, base: 0, claimed: false, money: 400, item: "gloss", count: 2, live: true },
    { kind: "raidDamage", target: 300, base: 0, claimed: false, money: 450, item: "myth", count: 1, live: true },
    { kind: "market", target: 1, base: 0, claimed: false, money: 300, item: "gloss", count: 1, live: true },
  ];
  missions.push(live[hashSeed(day) % live.length]!);
  for (const m of missions) m.base = statFor(state, m);
  return missions;
}

/**
 * Start a new day if the calendar moved: fresh missions and the streak advanced (yesterday's
 * login) or reset. Returns true when a new day began (the streak reward is then unpaid).
 */
export function rollDay(state: Goals & { daily: Daily | null }, day: string): boolean {
  const old = state.daily;
  if (old?.day === day) return false;
  const streak = old && old.day === prevDay(day) ? old.streak + 1 : 1;
  state.daily = { day, missions: makeMissions(state, day), streak, streakClaimed: false, bonusClaimed: false };
  state.stats.bestStreak = Math.max(state.stats.bestStreak, streak);
  return true;
}

export function normalizeDaily(raw: unknown): Daily | null {
  if (!raw || typeof raw !== "object") return null;
  const d = raw as Partial<Daily>;
  if (typeof d.day !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(d.day) || !Array.isArray(d.missions)) return null;
  const kinds: MissionKind[] = ["catch", "catchType", "wildWins", "superHits", "conditions", "steps", "rests", "visit", "duelWins", "raidDamage", "market"];
  const missions = d.missions.slice(0, 4).filter((m): m is Mission => !!m && kinds.includes(m.kind)).map((m) => ({
    kind: m.kind,
    target: Math.max(1, count(m.target)),
    base: count(m.base),
    ...(TYPE_IDS.includes(m.type as TypeId) ? { type: m.type } : {}),
    ...(ZONE_IDS.includes(m.zone as ZoneId) ? { zone: m.zone } : {}),
    money: Math.min(5000, count(m.money)),
    ...(typeof m.item === "string" ? { item: m.item, count: Math.min(5, Math.max(1, count(m.count))) } : {}),
    claimed: !!m.claimed,
    ...(m.live ? { live: true } : {}),
  }));
  return { day: d.day, missions, streak: Math.max(1, count(d.streak)), streakClaimed: !!d.streakClaimed, bonusClaimed: !!d.bonusClaimed };
}

/** Same day: keep the local missions but honour a claim made on either copy; else the later day. */
export function mergeDaily(local: Daily | null, remote: Daily | null): Daily | null {
  if (!local || !remote) return local ?? remote;
  if (local.day !== remote.day) return local.day > remote.day ? local : remote;
  return {
    ...local,
    streak: Math.max(local.streak, remote.streak),
    streakClaimed: local.streakClaimed || remote.streakClaimed,
    bonusClaimed: local.bonusClaimed || remote.bonusClaimed,
    // Same day, same seed: the lists match; a claim only carries over onto the same mission.
    missions: local.missions.map((m, i) => ({ ...m, claimed: m.claimed || (remote.missions[i]?.kind === m.kind && !!remote.missions[i]?.claimed) })),
  };
}

// ------------------------------------------------------------------------------------------------
// Badges and titles
// ------------------------------------------------------------------------------------------------

type BadgeState = Goals & { caught: SpeciesId[]; defeatedTrainers: string[]; money: number; encore?: Partial<Record<string, number>> };

export interface BadgeDef {
  id: string;
  name: string;
  desc: string;
  title: string;
  money: number;
  /** Progress toward the goal as [have, need]. */
  progress(s: BadgeState): [number, number];
}

const has = (n: number, need: number): [number, number] => [Math.min(n, need), need];
const beat = (s: BadgeState, ids: string[]): [number, number] => has(ids.filter((id) => s.defeatedTrainers.includes(id)).length, ids.length);

export const BADGES: BadgeDef[] = [
  { id: "firstCatch", name: "First Catch", desc: "Catch a creature.", title: "Rookie", money: 100, progress: (s) => has(s.stats.caught, 1) },
  { id: "collector", name: "Collector", desc: "Own 25 species in the Index.", title: "Collector", money: 500, progress: (s) => has(s.caught.length, 25) },
  { id: "curator", name: "Curator", desc: "Own 75 species in the Index.", title: "Curator", money: 1500, progress: (s) => has(s.caught.length, 75) },
  { id: "memeLord", name: "Meme Lord", desc: `Own all ${SPECIES_IDS.length} species.`, title: "Meme Lord", money: 10000, progress: (s) => has(s.caught.length, SPECIES_IDS.length) },
  { id: "shiny", name: "Shiny Hunter", desc: "Catch a shiny creature.", title: "Shiny Hunter", money: 1000, progress: (s) => has(s.stats.shinies, 1) },
  { id: "champion", name: "Champion", desc: "Clear Vell's trial and the circuit.", title: "Champion", money: 1000, progress: (s) => beat(s, ["vell", "rook", "mica", "nix"]) },
  { id: "frontier", name: "Frontier Hero", desc: "Beat every Frontier duelist.", title: "Frontier Hero", money: 1500, progress: (s) => beat(s, ["kade", "frida", "morrow", "quartz", "cinder"]) },
  { id: "undernet", name: "Skykeeper", desc: "Beat every Undernet duelist.", title: "Skykeeper", money: 2500, progress: (s) => beat(s, ["opal", "nami", "vex", "lune"]) },
  { id: "sidechain", name: "Chainwalker", desc: "Beat every Sidechain duelist.", title: "Chainwalker", money: 3500, progress: (s) => beat(s, ["juno", "ash", "kiri", "hana", "osric"]) },
  { id: "maxed", name: "Maxed Out", desc: "Raise a creature to Lv50.", title: "Maxed", money: 1500, progress: (s) => has(Math.max(0, ...[...s.party, ...s.pc].map((m) => m.level)), 50) },
  { id: "explorer", name: "Explorer", desc: "Set foot in every zone.", title: "Explorer", money: 800, progress: (s) => has(ZONE_IDS.filter((z) => (s.stats.zoneVisits[z] ?? 0) > 0).length, ZONE_IDS.length) },
  { id: "hexer", name: "Hexer", desc: "Inflict 50 battle conditions.", title: "Hexer", money: 600, progress: (s) => has(s.stats.conditions, 50) },
  { id: "bruiser", name: "Type Master", desc: "Land 200 super-effective hits.", title: "Type Master", money: 600, progress: (s) => has(s.stats.superHits, 200) },
  { id: "grinder", name: "Grinder", desc: "Win 100 wild battles.", title: "Grinder", money: 600, progress: (s) => has(s.stats.wildWins, 100) },
  { id: "whale", name: "Whale", desc: "Hold 20,000 coins at once.", title: "Whale", money: 0, progress: (s) => has(s.money, 20000) },
  { id: "loyal", name: "Loyal", desc: "Reach a 7-day login streak.", title: "Loyal", money: 700, progress: (s) => has(s.stats.bestStreak, 7) },
  { id: "diligent", name: "Diligent", desc: "Complete 20 daily missions.", title: "Diligent", money: 1000, progress: (s) => has(s.stats.missionsDone, 20) },
  { id: "duelist", name: "Duelist", desc: "Win a live duel.", title: "Duelist", money: 400, progress: (s) => has(s.stats.duelWins, 1) },
  { id: "gladiator", name: "Gladiator", desc: "Win 25 live duels.", title: "Gladiator", money: 2500, progress: (s) => has(s.stats.duelWins, 25) },
  { id: "raider", name: "Raider", desc: "Deal 2,000 raid damage.", title: "Raider", money: 800, progress: (s) => has(s.stats.raidDamage, 2000) },
  { id: "bossSlayer", name: "Boss Slayer", desc: "Help bring down 5 raid bosses.", title: "Boss Slayer", money: 2000, progress: (s) => has(s.stats.raidWins, 5) },
  { id: "trader", name: "Trader", desc: "Complete a trade with another scout.", title: "Trader", money: 300, progress: (s) => has(s.stats.trades, 1) },
  { id: "baron", name: "Market Baron", desc: "Sell 5 creatures on the Market.", title: "Baron", money: 1000, progress: (s) => has(s.stats.marketSales, 5) },
  { id: "encore", name: "Encore", desc: `After Osric, win an encore against all ${ENCORE_IDS.length} returning trainers.`, title: "Grand Champion", money: 5000,
    progress: (s) => has(ENCORE_IDS.filter((id) => s.encore?.[id] !== undefined).length, ENCORE_IDS.length) },
  { id: "mythHunter", name: "Myth Hunter", desc: `Catch all ${HUNTS.length} legends.`, title: "Myth Hunter", money: 4000,
    progress: (s) => has(HUNTS.filter((h) => s.caught.includes(h.legend)).length, HUNTS.length) },
];

export const TITLES = BADGES.map((b) => b.title);

export function badgeDone(s: BadgeState, b: BadgeDef): boolean {
  const [have, need] = b.progress(s);
  return have >= need;
}

/** Newly earned badges (not yet in `badges`), in list order. */
export function newBadges(s: BadgeState & { badges: string[] }): BadgeDef[] {
  return BADGES.filter((b) => !s.badges.includes(b.id) && badgeDone(s, b));
}

export function normalizeBadges(raw: unknown): string[] {
  return Array.isArray(raw) ? [...new Set(raw.filter((id): id is string => BADGES.some((b) => b.id === id)))] : [];
}

export function normalizeTitle(raw: unknown, badges: string[]): string {
  if (typeof raw !== "string" || !raw) return "";
  const b = BADGES.find((x) => x.title === raw);
  return b && badges.includes(b.id) ? raw : "";
}

// ------------------------------------------------------------------------------------------------
// Shinies
// ------------------------------------------------------------------------------------------------

/** One wild creature in 256 is shiny, read off its uid so no dice are spent on it. */
export const SHINY_ODDS = 256;

export function shinyFromUid(uid: string): boolean {
  let h = 2166136261;
  for (let i = 0; i < uid.length; i++) {
    h ^= uid.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) % SHINY_ODDS === 0;
}

